import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';

// Redux
import { navigate, setBrowserTitle, setTitle } from 'src/app/actions/core.actions';
import {
  findCommonIdUserByCommonId,
  findCommonIdUserByEmail,
  initializeFindCommonIdUserByCommonId,
  initializeFindCommonIdUserByEmail
} from 'src/app/actions/common-id/common-id-user.actions';
import { commonIdFindPurchases, initializeCommonIdFindPurchasesState } from 'src/app/actions/common-id/common-id-purchases.actions';
import {
  getCommonIdUser,
  getCommonIdUserSearching,
  getCommonIdUserNotFoundFlag
} from 'src/app/selectors/common-id/common-id-user.selectors';
import { getCommonIdPurchases } from 'src/app/selectors/common-id/common-id-purchases.selectors';
import { RootState } from 'src/app/reducers';

// models
import { CommonIdUser } from 'src/app/models/common-id/common-id-user';
import { CommonIdPurchases } from 'src/app/models/common-id/common-id-purchases';
import { Breadcrumb, MenuTab } from 'src/app/models/menu';
import { AccountTableData } from 'src/app/models/account-table-data';

// utils
import { Log } from 'src/app/utils/log';
import { Dates } from 'src/app/utils/dates';
import { CommonIdPurchaseUtil } from 'src/app/utils/common-id/common-id-purchase-util';

// config
import { PURCHASE_PLAN_NAMES, PURCHASE_STATUS_NAMES, COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR } from 'src/app/resources/common-id-config';

// others
import { RoutingPathResolver } from '../../../../app-routing-path-resolver';

type MenuType = 'email' | 'userId';

@Component({
  selector: 'app-common-id-account-search',
  templateUrl: './account-search.component.html',
  styleUrls: ['./account-search.component.scss']
})
export class CommonIdAccountSearchComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;
  title = '個人アカウント検索';

  account: AccountTableData[];
  commonIdUser$: Observable<CommonIdUser>;
  commonIdUser: CommonIdUser;
  isCommonIdUserSearching$: Observable<boolean>;
  isCommonIdUserNotFound$: Observable<boolean>;
  isResultDisplayed = false;
  isSearchButtonDisabled = true;

  filterForm: UntypedFormGroup;

  emailPlaceholder = 'メールアドレスで検索';
  enteredEmail: string;

  userIdPlaceholder = 'ユーザーIDで検索';
  enteredUserId: string;

  EMAIL_MENU_LABEL = 'メールアドレスで検索';
  USER_ID_MENU_LABEL = 'ユーザーIDで検索';
  breadcrumbsLabel: string;
  breadcrumbs: Breadcrumb[];
  menus: MenuTab[];
  selectedMenu: MenuType = 'email';

  private searchSubscription: Subscription;
  private lastPaymentMethodSubscription: Subscription;

  defaultDisplayedColumns: string[] = [
    'isActive',
    'email',
    'commonId',
    'purchaseStatus',
    'lastPurchasedAt',
    'lastPaymentMethod',
    'premiumEndAt',
    'purchasePlan',
    'latestAppLaunchedDate',
    'updatePurchaseStatus',
    'viewPurchaseHistory'
  ];
  displayedColumns: string[] = [...this.defaultDisplayedColumns];

  ACCOUNT_SEARCH_PATH: string;
  ACCOUNT_SEARCH_EMAIL_MENU_KEY: string;
  ACCOUNT_SEARCH_USERID_MENU_KEY: string;

  constructor(private store: Store<RootState>) {}

  ngOnInit() {
    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));
    this.setUpPath();
    this.setUpBreadcrumbs();
    this.setUpTabMenus();
    this.setUpForms();
    this.resetForms();
    this.setUpCommonIdUser();
    this.setUpIsCommonIdUserSearching();
    this.setUpIsCommonIdUserNotFound();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeFindCommonIdUserByEmail());
    this.store.dispatch(initializeFindCommonIdUserByCommonId());
    this.store.dispatch(initializeCommonIdFindPurchasesState());
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
    if (this.lastPaymentMethodSubscription) this.lastPaymentMethodSubscription.unsubscribe();
  }

  breadcrumbsClickHandler(key) {
    switch (key) {
      case 'admin':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveAdmin() }));
        break;
    }
  }

  menuClickHandler(key) {
    this.resetFlags();
    this.resetForms();

    switch (key) {
      case this.ACCOUNT_SEARCH_EMAIL_MENU_KEY:
        this.selectedMenu = 'email';

        // breadcrumbsのラベルを設定
        this.breadcrumbsLabel = `${this.title} - ${this.EMAIL_MENU_LABEL}`;
        this.breadcrumbs[1].label = this.breadcrumbsLabel;

        // メニュータブのオンオフを切り替え
        this.menus[0].isActive = true;
        this.menus[1].isActive = false;
        break;

      case this.ACCOUNT_SEARCH_USERID_MENU_KEY:
        this.selectedMenu = 'userId';
        // breadcrumbsのラベルを設定
        this.breadcrumbsLabel = `${this.title} - ${this.USER_ID_MENU_LABEL}`;
        this.breadcrumbs[1].label = this.breadcrumbsLabel;

        // メニュータブのオンオフを切り替え
        this.menus[0].isActive = false;
        this.menus[1].isActive = true;
        break;
    }
  }

  onInputChanges(value) {
    this.isSearchButtonDisabled = value.length > 0 ? false : true;
  }

  search(selectedMenu: MenuType) {
    this.isResultDisplayed = false;
    if (selectedMenu === 'email') {
      this.enteredEmail = this.filterForm.get(selectedMenu).value;
      this.searchByEmail();
    } else {
      this.enteredUserId = this.filterForm.get(selectedMenu).value;
      this.searchByCommonId();
    }

    // 検索結果が見つからなかった場合は購読が完了しないため、再検索時と破棄時に明示的に解除する
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
    this.searchSubscription = combineLatest([this.commonIdUser$])
      .pipe(take(1))
      .subscribe(([commonIdUser]) => {
        this.account = [this.setUpAccountData(commonIdUser)];
        this.isResultDisplayed = true;
        this.setUpLastPaymentMethod(commonIdUser.id);
      });
  }

  resetFilter(selectedMenu: MenuType) {
    this.resetFlags();
    if (selectedMenu === 'email') {
      this.store.dispatch(initializeFindCommonIdUserByEmail());
    } else {
      this.store.dispatch(initializeFindCommonIdUserByCommonId());
    }
    this.resetForms();
  }

  private setUpAccountData(user: CommonIdUser): AccountTableData {
    const isActive = user.isActive === undefined ? true : user.isActive;
    const purchaseStatus = user.purchaseStatus
      ? PURCHASE_STATUS_NAMES.find(status => status.id === user.purchaseStatus).name
      : COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR;
    const purchasePlan = user.purchasePlanId
      ? PURCHASE_PLAN_NAMES.find(status => status.id === user.purchasePlanId).name
      : COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR;

    const lastPurchasedAt = user.lastPurchasedAt
      ? Dates.stdDateStringFromIso(user.lastPurchasedAt)
          .split(' ')
          .join('\n')
      : COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR;

    const premiumEndAt = user.premiumEndAt
      ? Dates.simple4YmdStringFromIso(user.premiumEndAt)
          .split(' ')
          .join('\n')
      : COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR;

    const latestAppLaunchedDate = user.latestAppLaunchedDate
      ? Dates.stdDateStringFromIso(user.latestAppLaunchedDate)
          .split(' ')
          .join('\n')
      : COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR;

    return {
      id: user.id,
      email: user.email,
      commonId: user.commonId,
      isActive,
      purchaseStatus,
      purchasePlan,
      lastPurchasedAt,
      // 購入情報は別途取得するため、ここでは未取得を表す値を入れておく
      lastPaymentMethod: COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR,
      premiumEndAt,
      latestAppLaunchedDate
    };
  }

  /**
   * 最終購入の決済方法を取得して検索結果に反映する。
   *
   * 購入情報の取得に失敗した場合は state が更新されず購読が発火しないため、
   * 検索結果の表示（isResultDisplayed）はこの処理の完了を待たせない。
   * 同じ理由で購読は完了しないので、再検索時と破棄時に明示的に解除する。
   */
  private setUpLastPaymentMethod(userId: string) {
    if (this.lastPaymentMethodSubscription) this.lastPaymentMethodSubscription.unsubscribe();

    this.store.dispatch(initializeCommonIdFindPurchasesState());
    this.store.dispatch(commonIdFindPurchases({ userId }));

    this.lastPaymentMethodSubscription = this.store
      .select(getCommonIdPurchases)
      .pipe(
        // 購読開始時点の値は前回の検索の購入情報である可能性があるため捨てる。
        // このメソッドは search() の store 購読内から呼ばれるため、直前の dispatch は
        // NgRx の queueScheduler によって後回しになり、まだ state に反映されていない。
        skip(1),
        filter<CommonIdPurchases>(it => it != null),
        take(1)
      )
      .subscribe(purchases => {
        const lastPaymentMethod = CommonIdPurchaseUtil.findLastPaymentMethodName(purchases);
        // mat-table を再描画させるため配列・要素とも新しい参照に差し替える
        this.account = [{ ...this.account[0], lastPaymentMethod: lastPaymentMethod ?? COMMON_ID_ACCOUNT_NO_DATA_INDICATIOR }];
      });
  }

  private setUpPath() {
    this.ACCOUNT_SEARCH_PATH = RoutingPathResolver.resolveCommonIdAccountSearch();
    this.ACCOUNT_SEARCH_EMAIL_MENU_KEY = `${this.ACCOUNT_SEARCH_PATH}/email`;
    this.ACCOUNT_SEARCH_USERID_MENU_KEY = `${this.ACCOUNT_SEARCH_PATH}/userId`;
  }

  private setUpBreadcrumbs() {
    this.breadcrumbsLabel = `${this.title} - ${this.EMAIL_MENU_LABEL}`;
    this.breadcrumbs = [
      { key: 'admin', label: 'Admin機能' },
      { key: this.ACCOUNT_SEARCH_PATH, label: this.breadcrumbsLabel }
    ];
  }

  private setUpTabMenus() {
    this.menus = [
      {
        key: this.ACCOUNT_SEARCH_EMAIL_MENU_KEY,
        icon: 'mail_outline',
        label: this.EMAIL_MENU_LABEL,
        isActive: true
      },
      {
        key: this.ACCOUNT_SEARCH_USERID_MENU_KEY,
        icon: 'badge',
        fontSet: 'material-icons-outlined',
        label: this.USER_ID_MENU_LABEL,
        isActive: false
      }
    ];
  }

  private setUpForms() {
    this.filterForm = new UntypedFormGroup({
      email: new UntypedFormControl(),
      userId: new UntypedFormControl()
    });
  }

  private resetForms() {
    this.filterForm.get('email').setValue('');
    this.enteredEmail = '';
    this.filterForm.get('userId').setValue('');
    this.enteredUserId = '';
  }

  private setUpCommonIdUser() {
    this.commonIdUser$ = this.store.select(getCommonIdUser).pipe(filter(it => it != null));
  }

  private setUpIsCommonIdUserSearching() {
    this.isCommonIdUserSearching$ = this.store.select(getCommonIdUserSearching);
  }

  private setUpIsCommonIdUserNotFound() {
    this.isCommonIdUserNotFound$ = this.store.select(getCommonIdUserNotFoundFlag);
  }

  private searchByEmail() {
    Log.debug(this.LOG_SOURCE, `入力されたメールアドレス: ${this.enteredEmail}`);
    // アカウント情報の取得
    this.store.dispatch(initializeFindCommonIdUserByEmail());
    this.store.dispatch(findCommonIdUserByEmail({ email: this.enteredEmail }));
  }

  private searchByCommonId() {
    Log.debug(this.LOG_SOURCE, `入力されたユーザーID: ${this.enteredUserId}`);
    // アカウント情報の取得
    this.store.dispatch(initializeFindCommonIdUserByCommonId());
    this.store.dispatch(findCommonIdUserByCommonId({ commonId: this.enteredUserId }));
  }

  private resetFlags() {
    this.isResultDisplayed = false;
    this.isSearchButtonDisabled = true;
  }
}
